/**
 * Answering-machine detection (AMD) helpers for the Power Dialer.
 * Pure functions only — see amd.test.js.
 */

export const AMD_MACHINE_ACTIONS = [
  {
    value: "text",
    label: "Send a text and hang up",
    help: "The lead gets your text the moment voicemail picks up. Needs SMS set up.",
  },
  {
    value: "voicemail",
    label: "Leave a voicemail",
    help: "Plays one of your recordings after the beep.",
  },
  {
    value: "hangup",
    label: "Hang up",
    help: "Recommended. Logs it as voicemail and moves straight to the next lead.",
  },
  {
    value: "ask",
    label: "Ask me on each call",
    help: "Stay on the line and choose: text, leave a message, or hang up.",
  },
]

export const AMD_MODES = [
  {
    value: "listen_in",
    label: "Connect me immediately",
    help: "Detection runs in the background. People never hear a pause.",
  },
  {
    value: "screen_first",
    label: "Screen the call first",
    help: "You only ever hear people; the lead hears a short pause.",
  },
]

export const LEAD_FINAL_STATUSES = [
  "completed",
  "busy",
  "no-answer",
  "failed",
  "canceled",
]

export function amdActionLabel(value) {
  return (
    AMD_MACHINE_ACTIONS.find((item) => item.value === value)?.label || value
  )
}

export function isLeadFinalStatus(status) {
  return LEAD_FINAL_STATUSES.includes(status)
}

/** Should the call bar send DialerSessionId and poll AMD status? */
export function shouldUseAmd(contact) {
  return Boolean(contact?.dialerSessionId && contact?.amd?.enabled)
}

/** Chip shown in the call bar while the call is live. */
export function amdStatusChip(status) {
  if (!status) return { label: "Dialing…", color: "default" }
  const leadStatus = status.lead_call_status || ""
  const result = status.amd_result || ""
  const action = status.amd_action || ""

  if (result === "human") {
    return { label: "Person", color: "success" }
  }
  if (result === "machine") {
    switch (action) {
      case "texted":
        return { label: "Voicemail · text sent", color: "warning" }
      case "voicemail_dropped":
        return { label: "Voicemail · message left", color: "warning" }
      case "awaiting_agent":
        return { label: "Voicemail — your call", color: "error" }
      case "hung_up":
        return { label: "Voicemail · hung up", color: "warning" }
      default:
        return { label: "Voicemail", color: "warning" }
    }
  }
  if (result === "fax") {
    return { label: "Fax machine", color: "default" }
  }
  if (result === "unknown") {
    return { label: "Unsure — connected", color: "info" }
  }
  if (leadStatus === "ringing") {
    return { label: "Ringing…", color: "primary" }
  }
  if (leadStatus === "in-progress" || leadStatus === "answered") {
    return { label: "Detecting…", color: "primary" }
  }
  if (isLeadFinalStatus(leadStatus)) {
    return {
      label: leadStatus === "completed" ? "Ended" : "No answer",
      color: "default",
    }
  }
  return { label: "Dialing…", color: "default" }
}

/** Which manual buttons to show during a live AMD call. */
export function amdControls(status) {
  if (!status || !status.amd_enabled) {
    return {
      canPerson: false,
      canText: false,
      canDrop: false,
      canHangup: false,
    }
  }
  const leadActive = Boolean(status.lead_leg_active)
  const awaiting =
    status.amd_result === "machine" && status.amd_action === "awaiting_agent"
  return {
    canPerson: awaiting,
    canText: leadActive && Boolean(status.can_text),
    canDrop: leadActive && Boolean(status.can_drop_voicemail),
    canHangup: awaiting && leadActive,
  }
}

/** True when the backend already logged the attempt (machine handled). */
export function amdAutoLogged(status) {
  return Boolean(status?.amd_auto_logged)
}

/**
 * Keep polling until the call has a settled outcome: a person (or an unsure
 * result we hand to the agent), a fax, a finished lead leg, or a machine we
 * have already acted on.
 */
export function shouldKeepPollingAmd(status) {
  if (!status) return true
  const result = status.amd_result || ""
  if (result === "human" || result === "unknown" || result === "fax") {
    return false
  }
  if (!status.lead_leg_active && isLeadFinalStatus(status.lead_call_status)) {
    return false
  }
  return true
}

/** Preselect for the manual attempt-log dialog. */
export function amdInitialDisposition(status) {
  if (!status) return null
  if (status.amd_result === "machine") return "voicemail"
  if (status.amd_result === "fax") return "other"
  return null
}

/** Extra fields sent with dialer advance so the queue shows AMD outcomes. */
export function amdAdvanceExtra(status) {
  if (!status) return {}
  const extra = {}
  if (status.amd_result) extra.amd_result = status.amd_result
  if (status.amd_action) extra.amd_action = status.amd_action
  return extra
}

/** Snackbar text after an auto-handled voicemail. */
export function amdOutcomeMessage(status) {
  if (!status) return ""
  switch (status.amd_action) {
    case "texted":
      return "Voicemail detected — text sent."
    case "voicemail_dropped":
      return "Voicemail detected — message left."
    case "fax_hung_up":
      return "Fax machine detected — hung up."
    case "hung_up":
      if ((status.amd_action_detail || "").startsWith("quiet_hours")) {
        return "Voicemail detected — hung up (quiet hours, no text sent)."
      }
      if ((status.amd_action_detail || "").startsWith("text_failed")) {
        return "Voicemail detected — hung up (text could not be sent)."
      }
      return "Voicemail detected — hung up."
    default:
      return "Voicemail detected."
  }
}

/** Chip for a queue row in the Power Dialer sidebar. */
export function queueAmdChip(item) {
  if (!item) return null
  if (item.amd_action === "texted") {
    return { label: "Voicemail · Texted", color: "warning" }
  }
  if (item.amd_action === "voicemail_dropped") {
    return { label: "Voicemail · Left msg", color: "warning" }
  }
  if (item.amd_result === "machine") {
    return { label: "Voicemail", color: "warning" }
  }
  return null
}

/** One-line summary for the sidebar header. */
export function amdSessionSummary(amd) {
  if (!amd?.enabled) return ""
  const action = amd.machine_action
  if (action === "text") {
    const name = amd.sms_campaign?.name
    return name ? `On voicemail: text “${name}”` : "On voicemail: send a text"
  }
  if (action === "voicemail") {
    const name = amd.voicemail_recording?.name
    return name
      ? `On voicemail: play “${name}”`
      : "On voicemail: leave a message"
  }
  if (action === "hangup") return "On voicemail: hang up"
  if (action === "ask") return "On voicemail: ask me"
  return "Answering machine detection on"
}

/** Initial form state for the confirm dialog from the preview's amd block. */
export function amdFormFromOptions(options) {
  const defaults = options?.defaults || {}
  return {
    enabled: Boolean(defaults.enabled),
    machine_action: defaults.machine_action || "hangup",
    mode: defaults.mode || "listen_in",
    sms_campaign_id: defaults.sms_campaign?.id ?? null,
    voicemail_recording_id: defaults.voicemail_recording?.id ?? null,
  }
}

/** Why the session cannot start with the current AMD choices (or null). */
export function amdFormBlocker(form, options) {
  if (!form?.enabled) return null
  if (form.machine_action === "text") {
    if (!form.sms_campaign_id)
      return "Choose the text to send when voicemail picks up."
    if (options && options.sms_ready === false) {
      return "Texting is not set up for this account yet. Finish SMS setup in Settings or pick another voicemail action."
    }
  }
  if (form.machine_action === "voicemail" && !form.voicemail_recording_id) {
    return "Choose the voicemail recording to play."
  }
  return null
}

/** Body sent to POST /crm/api/dialer/sessions/ as `amd`. */
export function buildAmdSessionPayload(form) {
  if (!form || !form.enabled) return { enabled: false }
  return {
    enabled: true,
    machine_action: form.machine_action || "hangup",
    mode: form.mode || "listen_in",
    sms_campaign_id: form.sms_campaign_id ?? null,
    voicemail_recording_id: form.voicemail_recording_id ?? null,
  }
}
